import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Loader } from "@/components/ui-bits";
import api from "@/lib/api";

import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Onboarding from "@/pages/Onboarding";
import Home from "@/pages/Home";
import Explore from "@/pages/Explore";
import PassionDetail from "@/pages/PassionDetail";
import Communities from "@/pages/Communities";
import CommunityDetail from "@/pages/CommunityDetail";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import Challenges from "@/pages/Challenges";
import ChallengeDetail from "@/pages/ChallengeDetail";
import Marketplace from "@/pages/Marketplace";
import ListingDetail from "@/pages/ListingDetail";
import ProjectDetail from "@/pages/ProjectDetail";
import MyPassion from "@/pages/MyPassion";
import PublicProfile from "@/pages/PublicProfile";
import BuilderDashboard from "@/pages/BuilderDashboard";
import SearchPage from "@/pages/SearchPage";
import Saved from "@/pages/Saved";
import { CreateHub, CreateCommunity, CreateEvent, CreateChallenge, CreateProject, CreateListing } from "@/pages/Create";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";

function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { googleSession } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = location.hash || "";
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const user = await googleSession(sid);
        window.history.replaceState(null, "", window.location.pathname);
        nav(user.onboarded ? "/home" : "/onboarding", { replace: true });
      } catch {
        nav("/login", { replace: true });
      }
    })();
  }, []); // eslint-disable-line
  return <div className="min-h-screen grid place-items-center bg-[#FFFDF8]"><Loader label="Signing you in" /></div>;
}

function Protected({ children, requireOnboard = true }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center"><Loader /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (requireOnboard && !user.onboarded) return <Navigate to="/onboarding" replace />;
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center"><Loader /></div>;
  if (user) return <Navigate to={user.onboarded ? "/home" : "/onboarding"} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
      <Route path="/login" element={<PublicOnly><Auth mode="login" /></PublicOnly>} />
      <Route path="/signup" element={<PublicOnly><Auth mode="signup" /></PublicOnly>} />
      <Route path="/onboarding" element={<OnboardingRoute />} />

      <Route path="/home" element={<Protected><Home /></Protected>} />
      <Route path="/explore" element={<Protected><Explore /></Protected>} />
      <Route path="/passions/:slug" element={<Protected><PassionDetail /></Protected>} />
      <Route path="/communities" element={<Protected><Communities /></Protected>} />
      <Route path="/communities/:id" element={<Protected><CommunityDetail /></Protected>} />
      <Route path="/events" element={<Protected><Events /></Protected>} />
      <Route path="/events/:id" element={<Protected><EventDetail /></Protected>} />
      <Route path="/challenges" element={<Protected><Challenges /></Protected>} />
      <Route path="/challenges/:id" element={<Protected><ChallengeDetail /></Protected>} />
      <Route path="/marketplace" element={<Protected><Marketplace /></Protected>} />
      <Route path="/marketplace/:id" element={<Protected><ListingDetail /></Protected>} />
      <Route path="/projects/:id" element={<Protected><ProjectDetail /></Protected>} />
      <Route path="/me" element={<Protected><MyPassion /></Protected>} />
      <Route path="/u/:id" element={<Protected><PublicProfile /></Protected>} />
      <Route path="/dashboard" element={<Protected><BuilderDashboard /></Protected>} />
      <Route path="/search" element={<Protected><SearchPage /></Protected>} />
      <Route path="/saved" element={<Protected><Saved /></Protected>} />

      <Route path="/create" element={<Protected><CreateHub /></Protected>} />
      <Route path="/create/community" element={<Protected><CreateCommunity /></Protected>} />
      <Route path="/create/event" element={<Protected><CreateEvent /></Protected>} />
      <Route path="/create/challenge" element={<Protected><CreateChallenge /></Protected>} />
      <Route path="/create/project" element={<Protected><CreateProject /></Protected>} />
      <Route path="/create/listing" element={<Protected><CreateListing /></Protected>} />

      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function OnboardingRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center"><Loader /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboarded) return <Navigate to="/home" replace />;
  return <Onboarding />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
